<template>
  <div class="sport-reg-root">
    <MainNavbar />
    <div class="sport-reg-page">
      <router-link :to="{ name: 'MatchesHub' }" class="back-to-hub">
        <i class="fa-solid fa-arrow-left" aria-hidden="true" />
        Back to Match Hub
      </router-link>

      <header class="sport-reg-header">
        <p class="sport-reg-kicker">
          <i :class="theme.icon" aria-hidden="true" />
          {{ theme.kicker }}
        </p>
        <h1 class="sport-reg-title">{{ theme.title }}</h1>
        <p class="sport-reg-lead">{{ theme.lead }}</p>
      </header>

      <div class="sport-reg-card">
        <div class="sport-reg-steps" role="list" aria-label="Registration progress">
          <div
            v-for="(step, index) in steps"
            :key="step.label"
            class="sport-reg-step"
            role="listitem"
            :class="{ active: currentStep === index + 1, done: currentStep > index + 1 }"
          >
            <span class="sport-reg-step__num">{{ index + 1 }}</span>
            <span class="sport-reg-step__label">{{ step.label }}</span>
          </div>
        </div>

        <!-- Step 1: Squad identity -->
        <section v-show="currentStep === 1" class="sport-reg-panel" aria-labelledby="sport-reg-step1-title">
          <h2 id="sport-reg-step1-title" class="sport-reg-panel__title">Squad identity</h2>
          <p class="sport-reg-panel__desc">Team name, contact, format, and how you play.</p>

          <form class="sport-reg-form" @submit.prevent="validateStep1">
            <div class="sport-reg-form-grid">
              <div class="sport-reg-field">
                <label for="teamName">Team name *</label>
                <input
                  id="teamName"
                  v-model.trim="form.team.teamName"
                  type="text"
                  placeholder="e.g. Impala RFC"
                  :class="{ error: errors.teamName }"
                />
                <span v-if="errors.teamName" class="sport-reg-error">{{ errors.teamName }}</span>
              </div>

              <div class="sport-reg-field">
                <label for="teamPhone">Team phone *</label>
                <div class="sport-reg-phone-wrap" :class="{ error: errors.teamPhone }">
                  <span class="sport-reg-phone-prefix" aria-hidden="true">+254</span>
                  <input
                    id="teamPhone"
                    type="tel"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    maxlength="9"
                    class="sport-reg-phone-national"
                    :value="form.team.teamPhoneNational"
                    placeholder="712345678"
                    autocomplete="tel-national"
                    @input="onTeamPhoneInput"
                  />
                </div>
                <p class="sport-reg-hint">
                  Kenyan mobile: +254 plus 9 digits (e.g. 712345678). Shown to opponents for scheduling.
                </p>
                <span v-if="errors.teamPhone" class="sport-reg-error">{{ errors.teamPhone }}</span>
              </div>

              <div class="sport-reg-field">
                <label for="gameFormat">Game format *</label>
                <select id="gameFormat" v-model="form.team.gameFormat" :class="{ error: errors.gameFormat }">
                  <option value="" disabled>Select format</option>
                  <option value="15s">15-a-side (XV)</option>
                  <option value="7s">7-a-side</option>
                  <option value="10s">10-a-side</option>
                  <option value="both">Multiple formats</option>
                </select>
                <span v-if="errors.gameFormat" class="sport-reg-error">{{ errors.gameFormat }}</span>
              </div>

              <div class="sport-reg-field">
                <label for="rosterTarget">Target roster size *</label>
                <select id="rosterTarget" v-model="form.team.rosterTarget" :class="{ error: errors.rosterTarget }">
                  <option value="" disabled>Players on squad</option>
                  <option v-for="n in rosterOptions" :key="n" :value="String(n)">{{ n }} players</option>
                </select>
                <p class="sport-reg-hint">XV squads often carry 22–30 players; 7s teams typically run 12–14.</p>
                <span v-if="errors.rosterTarget" class="sport-reg-error">{{ errors.rosterTarget }}</span>
              </div>

              <div class="sport-reg-field">
                <label for="teamType">Team type *</label>
                <select id="teamType" v-model="form.team.teamType" :class="{ error: errors.teamType }">
                  <option value="" disabled>Select type</option>
                  <option value="preschool">Pre-School</option>
                  <option value="highschool">High School</option>
                  <option value="academy">Academy / Youth</option>
                  <option value="recreational">Recreational / pick-up</option>
                  <option value="competitive">Competitive league</option>
                  <option value="corporate">Corporate</option>
                  <option value="community">Community club</option>
                  <option value="club">Registered club</option>
                </select>
                <span v-if="errors.teamType" class="sport-reg-error">{{ errors.teamType }}</span>
              </div>

              <div class="sport-reg-field sport-reg-field--logo">
                <label>Team logo</label>
                <button type="button" class="sport-reg-logo-btn" @click="triggerLogoUpload">
                  <img v-if="logoPreview" :src="logoPreview" alt="" class="sport-reg-logo-preview" />
                  <span v-else><i class="fa-solid fa-camera" /> Upload logo</span>
                </button>
                <input ref="logoInput" type="file" accept="image/*" class="sr-only" @change="handleLogoUpload" />
                <p class="sport-reg-hint">Square PNG or JPG, max 2MB</p>
              </div>
            </div>

            <div class="sport-reg-field sport-reg-field--full">
              <label for="teamBio">Team bio *</label>
              <textarea
                id="teamBio"
                v-model.trim="form.team.teamBio"
                rows="4"
                maxlength="500"
                placeholder="Playing style, league history, what you are looking for..."
                :class="{ error: errors.teamBio }"
              />
              <span class="sport-reg-counter">{{ form.team.teamBio.length }}/500</span>
              <span v-if="errors.teamBio" class="sport-reg-error">{{ errors.teamBio }}</span>
            </div>

            <p class="sport-reg-notice sport-reg-footnote">
              <i class="fa-solid fa-user-group" aria-hidden="true" />
              You do not need to add players during registration. After your team is created, invite players from
              your team dashboard—they can join your squad and you can track their progress over time.
            </p>

            <div class="sport-reg-nav">
              <span />
              <button type="submit" class="sport-reg-btn sport-reg-btn--primary">
                Continue <i class="fa-solid fa-arrow-right" />
              </button>
            </div>
          </form>
        </section>

        <!-- Step 2: Pitch & schedule -->
        <section v-show="currentStep === 2" class="sport-reg-panel" aria-labelledby="sport-reg-step2-title">
          <h2 id="sport-reg-step2-title" class="sport-reg-panel__title">Pitch &amp; schedule</h2>
          <p class="sport-reg-panel__desc">Home ground, pitch surface, and when you are available.</p>

          <form class="sport-reg-form" @submit.prevent="validateStep2">
            <div class="sport-reg-form-grid">
              <div class="sport-reg-field">
                <label for="homeCourt">Home pitch / club ground *</label>
                <input
                  id="homeCourt"
                  v-model.trim="form.team.homeCourt"
                  type="text"
                  placeholder="e.g. RFUEA Ground, Ngong Road"
                  :class="{ error: errors.homeCourt }"
                />
                <span v-if="errors.homeCourt" class="sport-reg-error">{{ errors.homeCourt }}</span>
              </div>

              <div class="sport-reg-field sport-reg-field--full">
                <label for="courtPinLocation">
                  <i class="fa-solid fa-map-pin" aria-hidden="true" />
                  Ground pin / map link
                </label>
                <input
                  id="courtPinLocation"
                  v-model.trim="form.team.courtPinLocation"
                  type="text"
                  placeholder="Google Maps link, plus code, or coordinates (lat, lng)"
                  :class="{ error: errors.courtPinLocation }"
                />
                <p class="sport-reg-hint">
                  Helps opponents find your pitch for fixtures and walk-throughs.
                </p>
                <span v-if="errors.courtPinLocation" class="sport-reg-error">{{ errors.courtPinLocation }}</span>
              </div>

              <div class="sport-reg-field">
                <label for="pitchSurface">Pitch surface *</label>
                <select id="pitchSurface" v-model="form.team.pitchSurface" :class="{ error: errors.pitchSurface }">
                  <option value="" disabled>Select surface</option>
                  <option value="grass">Natural grass</option>
                  <option value="artificial">Artificial / 3G turf</option>
                  <option value="both">Grass and artificial</option>
                </select>
                <span v-if="errors.pitchSurface" class="sport-reg-error">{{ errors.pitchSurface }}</span>
              </div>

              <div class="sport-reg-field">
                <label for="county">County *</label>
                <select id="county" v-model="form.team.county" :class="{ error: errors.county }" @change="onCountyChange">
                  <option value="" disabled>Select county</option>
                  <option v-for="c in availableCounties" :key="c" :value="c">{{ c }}</option>
                </select>
                <span v-if="errors.county" class="sport-reg-error">{{ errors.county }}</span>
              </div>

              <div class="sport-reg-field">
                <label for="constituency">Constituency *</label>
                <select
                  id="constituency"
                  v-model="form.team.constituency"
                  :disabled="!form.team.county"
                  :class="{ error: errors.constituency }"
                >
                  <option value="" disabled>
                    {{ form.team.county ? "Select constituency" : "Select county first" }}
                  </option>
                  <option v-for="c in availableConstituencies" :key="c" :value="c">{{ c }}</option>
                </select>
                <span v-if="errors.constituency" class="sport-reg-error">{{ errors.constituency }}</span>
              </div>

              <div class="sport-reg-field">
                <label for="matchDuration">Typical match length *</label>
                <select id="matchDuration" v-model="form.team.matchDuration" :class="{ error: errors.matchDuration }">
                  <option value="" disabled>Select duration</option>
                  <option value="80min">80 minutes (2 × 40)</option>
                  <option value="70min">70 minutes (2 × 35)</option>
                  <option value="7s_14min">7s — 14 minutes (2 × 7)</option>
                  <option value="flexible">Flexible / friendlies</option>
                </select>
                <span v-if="errors.matchDuration" class="sport-reg-error">{{ errors.matchDuration }}</span>
              </div>

              <div class="sport-reg-field">
                <label for="skillLevel">Skill level *</label>
                <select id="skillLevel" v-model="form.team.skillLevel" :class="{ error: errors.skillLevel }">
                  <option value="" disabled>Select level</option>
                  <option value="beginner">Beginner — learning fundamentals</option>
                  <option value="intermediate">Intermediate — regular pick-up / league</option>
                  <option value="advanced">Advanced — competitive league</option>
                  <option value="elite">Elite — former college / pro experience</option>
                </select>
                <span v-if="errors.skillLevel" class="sport-reg-error">{{ errors.skillLevel }}</span>
              </div>
            </div>

            <div class="sport-reg-field sport-reg-field--full">
              <label>Preferred practice / game days</label>
              <div class="sport-reg-pills">
                <button
                  v-for="day in daysOfWeek"
                  :key="day.value"
                  type="button"
                  class="sport-reg-pill"
                  :class="{ selected: form.preferences.preferredDays.includes(day.value) }"
                  @click="toggleDay(day.value)"
                >
                  {{ day.label }}
                </button>
              </div>
            </div>

            <div class="sport-reg-nav">
              <button type="button" class="sport-reg-btn sport-reg-btn--ghost" @click="currentStep--">
                <i class="fa-solid fa-arrow-left" /> Back
              </button>
              <button type="submit" class="sport-reg-btn sport-reg-btn--primary">
                Review <i class="fa-solid fa-arrow-right" />
              </button>
            </div>
          </form>
        </section>

        <!-- Step 3: Review -->
        <section v-show="currentStep === 3" class="sport-reg-panel" aria-labelledby="sport-reg-step3-title">
          <h2 id="sport-reg-step3-title" class="sport-reg-panel__title">Review &amp; submit</h2>
          <p class="sport-reg-panel__desc">Confirm your rugby team profile before we save it.</p>

          <dl class="sport-reg-review">
            <div class="sport-reg-review-row"><dt>Team</dt><dd>{{ form.team.teamName }}</dd></div>
            <div class="sport-reg-review-row"><dt>Phone</dt><dd>{{ teamPhoneDisplay }}</dd></div>
            <div class="sport-reg-review-row"><dt>Format</dt><dd>{{ formatLabel(form.team.gameFormat) }}</dd></div>
            <div class="sport-reg-review-row"><dt>Roster target</dt><dd>{{ form.team.rosterTarget }} players</dd></div>
            <div class="sport-reg-review-row"><dt>Type</dt><dd>{{ typeLabel(form.team.teamType) }}</dd></div>
            <div class="sport-reg-review-row"><dt>Home pitch</dt><dd>{{ form.team.homeCourt }}</dd></div>
            <div v-if="form.team.courtPinLocation" class="sport-reg-review-row">
              <dt>Court pin</dt>
              <dd>{{ form.team.courtPinLocation }}</dd>
            </div>
            <div class="sport-reg-review-row"><dt>Pitch</dt><dd>{{ pitchLabel(form.team.pitchSurface) }}</dd></div>
            <div class="sport-reg-review-row"><dt>Location</dt><dd>{{ form.team.county }}, {{ form.team.constituency }}</dd></div>
            <div class="sport-reg-review-row"><dt>Match length</dt><dd>{{ durationLabel(form.team.matchDuration) }}</dd></div>
            <div class="sport-reg-review-row"><dt>Skill</dt><dd>{{ skillLabel(form.team.skillLevel) }}</dd></div>
            <div class="sport-reg-review-row sport-reg-review-row--block"><dt>Bio</dt><dd>{{ form.team.teamBio }}</dd></div>
          </dl>

          <p class="sport-reg-notice sport-reg-footnote">
            <i class="fa-solid fa-envelope" aria-hidden="true" />
            Players are not added here. Once your team is saved, use <strong>Invite players</strong> on your team
            dashboard to build your roster and track player progress.
          </p>

          <div class="sport-reg-nav">
            <button type="button" class="sport-reg-btn sport-reg-btn--ghost" :disabled="isSubmitting" @click="currentStep--">
              <i class="fa-solid fa-arrow-left" /> Back
            </button>
            <button type="button" class="sport-reg-btn sport-reg-btn--primary" :disabled="isSubmitting" @click="submitRegistration">
              <i v-if="isSubmitting" class="fa-solid fa-spinner fa-spin" />
              {{ isSubmitting ? "Submitting..." : "Create rugby team" }}
            </button>
          </div>
        </section>
      </div>
    </div>
    <ThirdpartyFooter />
  </div>
</template>

<script>
import "@/routes/match_hub/shared/sportTeamSignUp.css";
import MainNavbar from "@/components/main/headernfooter/header.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import { useAuthStore } from "@/authen/auth";
import { buildSignInLocation } from "@/utils/authRedirect";
import { kenyaCounties } from "@/routes/match_hub/football/counties.js";
import { buildApiUrl } from "@/config/api";
import { SPORT_SIGNUP_THEME } from "@/routes/match_hub/shared/sportSignUpTheme";

const ROSTER_MIN = 15;
const ROSTER_MAX = 30;

export default {
  name: "RugbyTeamRegistration",

  components: { MainNavbar, ThirdpartyFooter },

  data() {
    return {
      theme: SPORT_SIGNUP_THEME.rugby,
      currentStep: 1,
      isSubmitting: false,
      logoPreview: null,
      logoFile: null,
      rosterOptions: Array.from({ length: ROSTER_MAX - ROSTER_MIN + 1 }, (_, i) => i + ROSTER_MIN),
      steps: [
        { label: "Squad" },
        { label: "Court" },
        { label: "Review" },
      ],
      daysOfWeek: [
        { value: "monday", label: "Mon" },
        { value: "tuesday", label: "Tue" },
        { value: "wednesday", label: "Wed" },
        { value: "thursday", label: "Thu" },
        { value: "friday", label: "Fri" },
        { value: "saturday", label: "Sat" },
        { value: "sunday", label: "Sun" },
      ],
      form: {
        sport: "rugby",
        team: {
          teamName: "",
          teamPhoneNational: "",
          teamType: "",
          teamBio: "",
          gameFormat: "",
          rosterTarget: "",
          homeCourt: "",
          courtPinLocation: "",
          pitchSurface: "",
          county: "",
          constituency: "",
          matchDuration: "",
          skillLevel: "",
        },
        preferences: { preferredDays: [] },
      },
      availableCounties: Object.keys(kenyaCounties),
      availableConstituencies: [],
      errors: {},
    };
  },

  computed: {
    teamPhoneDisplay() {
      const digits = (this.form.team.teamPhoneNational || "").replace(/\D/g, "");
      if (!digits) return "—";
      return `+254${digits}`;
    },
  },

  beforeMount() {
    sessionStorage.setItem("toafrica_match_sport", "rugby");
    const auth = useAuthStore();
    auth.checkToken();
    if (!auth.isAuthenticated) {
      this.$router.replace(buildSignInLocation(this.$route.fullPath));
    }
  },

  methods: {
    emitToast(title, message, type = "info") {
      window.dispatchEvent(new CustomEvent("show-toast", { detail: { title, message, type } }));
    },

    formatLabel(v) {
      const map = { "15s": "15-a-side", "7s": "7-a-side", "10s": "10-a-side", both: "Multiple formats" };
      return map[v] || v;
    },

    typeLabel(v) {
      const map = {
        preschool: "Pre-School",
        highschool: "High School",
        academy: "Academy / Youth",
        recreational: "Recreational",
        competitive: "Competitive",
        corporate: "Corporate",
        school: "School / college",
        community: "Community",
      };
      return map[v] || v;
    },

    pitchLabel(v) {
      const map = { grass: "Natural grass", artificial: "Artificial turf", both: "Grass & artificial" };
      return map[v] || v;
    },

    durationLabel(v) {
      const map = {
        "80min": "80 minutes",
        "70min": "70 minutes",
        "7s_14min": "7s (14 min)",
        flexible: "Flexible",
      };
      return map[v] || v;
    },

    skillLabel(v) {
      return v ? v.charAt(0).toUpperCase() + v.slice(1) : "";
    },

    onTeamPhoneInput(e) {
      const raw = (e.target?.value || "").replace(/\D/g, "").slice(0, 9);
      this.form.team.teamPhoneNational = raw;
      if (this.errors.teamPhone) {
        delete this.errors.teamPhone;
      }
    },

    isValidKenyanPhone(national) {
      const digits = (national || "").replace(/\D/g, "");
      return digits.length === 9 && /^7\d{8}$/.test(digits);
    },

    formatTeamPhoneE164(national) {
      const digits = (national || "").replace(/\D/g, "");
      if (!this.isValidKenyanPhone(digits)) return "";
      return `+254${digits}`;
    },

    onCountyChange() {
      this.form.team.constituency = "";
      this.availableConstituencies = kenyaCounties[this.form.team.county] || [];
    },

    toggleDay(day) {
      const days = this.form.preferences.preferredDays;
      const i = days.indexOf(day);
      if (i > -1) days.splice(i, 1);
      else days.push(day);
    },

    triggerLogoUpload() {
      this.$refs.logoInput?.click();
    },

    handleLogoUpload(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) {
        this.emitToast("Logo too large", "Max 2MB.", "error");
        return;
      }
      this.logoFile = file;
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.logoPreview = ev.target?.result;
      };
      reader.readAsDataURL(file);
    },

    clearErrors() {
      this.errors = {};
    },

    validateStep1() {
      this.clearErrors();
      const t = this.form.team;
      if (!t.teamName) this.errors.teamName = "Team name is required";
      if (!t.teamPhoneNational) {
        this.errors.teamPhone = "Phone is required";
      } else if (!this.isValidKenyanPhone(t.teamPhoneNational)) {
        this.errors.teamPhone =
          "Enter 9 digits after +254 (e.g. 712345678, starting with 7)";
      }
      if (!t.gameFormat) this.errors.gameFormat = "Select a game format";
      if (!t.rosterTarget) this.errors.rosterTarget = "Select roster size";
      else {
        const n = Number(t.rosterTarget);
        if (n < ROSTER_MIN || n > ROSTER_MAX) {
          this.errors.rosterTarget = `Roster must be ${ROSTER_MIN}-${ROSTER_MAX} players`;
        }
      }
      if (!t.teamType) this.errors.teamType = "Select team type";
      if (!t.teamBio || t.teamBio.length < 20) {
        this.errors.teamBio = "Bio must be at least 20 characters";
      }
      if (Object.keys(this.errors).length) return;
      this.currentStep = 2;
    },

    validateStep2() {
      this.clearErrors();
      const t = this.form.team;
      if (!t.homeCourt) this.errors.homeCourt = "Home pitch is required";
      if (!t.pitchSurface) this.errors.pitchSurface = "Select pitch surface";
      if (!t.county) this.errors.county = "County is required";
      if (!t.constituency) this.errors.constituency = "Constituency is required";
      if (!t.matchDuration) this.errors.matchDuration = "Select match duration";
      if (!t.skillLevel) this.errors.skillLevel = "Select skill level";
      if (Object.keys(this.errors).length) return;
      this.currentStep = 3;
    },

    formatTeamCreateErrorDetail(errorData, status, statusText) {
      const d = errorData && errorData.detail;
      if (typeof d === "string") return d;
      if (Array.isArray(d)) {
        return d
          .map((item) => {
            if (typeof item === "string") return item;
            if (item && typeof item.msg === "string") return item.msg;
            return null;
          })
          .filter(Boolean)
          .join(" ");
      }
      if (d && typeof d === "object" && typeof d.msg === "string") return d.msg;
      if (typeof errorData?.message === "string") return errorData.message;
      return statusText || `Request failed (${status})`;
    },

    buildTeamCreateErrorToast(status, rawDetail, teamName) {
      const detail = (rawDetail || "").trim() || "Something went wrong. Please try again.";
      let message = detail;
      let title = "Registration failed";

      if (status === 401) {
        title = "Sign in required";
        message = "Your session may have expired. Sign in again and retry.";
      } else if (status === 400) {
        if (/already have a team/i.test(detail)) title = "You already have a team";
        else if (/already on a team/i.test(detail)) title = "Already on a squad";
        else if (/name already exists/i.test(detail)) {
          title = "Team name taken";
          const base = teamName ? String(teamName).trim() : "";
          message = base
            ? `${detail} Try "${base} RFC" or "${base} XV".`
            : detail;
        } else if (/logo/i.test(detail)) title = "Logo issue";
        else title = "Cannot create team";
        message = detail;
      } else if (status === 422) {
        title = "Invalid form data";
        message = detail;
      } else if (status === 429) {
        title = "Too many attempts";
        message = detail || "Wait a moment and try again.";
      } else if (status >= 500) {
        title = "Server error";
        message = detail;
      }
      return { title, message };
    },

    handleSuccess(data) {
      const name = (data && data.team_name) || this.form.team.teamName;
      this.emitToast(
        "Rugby team created",
        name ? `"${name}" was registered successfully.` : "Your squad was created successfully.",
        "success",
      );
      this.$router.push({ name: "RugbyTeamDashboard" }).catch(() => {});
    },

    async submitRegistration() {
      this.isSubmitting = true;
      try {
        const token =
          localStorage.getItem("access_token") ||
          localStorage.getItem("token") ||
          sessionStorage.getItem("access_token") ||
          sessionStorage.getItem("token");

        if (!token) {
          this.emitToast(
            "Sign in required",
            "You need to be logged in to register a team.",
            "error",
          );
          return;
        }

        const t = this.form.team;
        const formData = new FormData();
        formData.append("team_name", t.teamName);
        formData.append("team_phone", this.formatTeamPhoneE164(t.teamPhoneNational));
        formData.append("team_type", t.teamType);
        formData.append("team_bio", t.teamBio);
        formData.append("game_format", t.gameFormat);
        formData.append("roster_target", String(t.rosterTarget));
        formData.append("home_ground", t.homeCourt);
        if (t.courtPinLocation) {
          formData.append("home_ground_pin_location", t.courtPinLocation);
        }
        formData.append("pitch_surface", t.pitchSurface);
        formData.append("county", t.county);
        formData.append("constituency", t.constituency);
        formData.append("match_duration", t.matchDuration);
        formData.append("skill_level", t.skillLevel);

        formData.append("members", JSON.stringify([]));
        formData.append(
          "preferences",
          JSON.stringify({ preferred_days: this.form.preferences.preferredDays }),
        );

        if (this.logoFile) {
          formData.append("logo", this.logoFile);
        }

        const response = await fetch(buildApiUrl("/api/teams/rugby/create"), {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (!response.ok) {
          let errorData = {};
          try {
            errorData = await response.json();
          } catch {
            /* non-JSON */
          }
          const rawDetail = this.formatTeamCreateErrorDetail(
            errorData,
            response.status,
            response.statusText,
          );
          const { title, message } = this.buildTeamCreateErrorToast(
            response.status,
            rawDetail,
            t.teamName,
          );
          this.emitToast(title, message, "error");
          return;
        }

        const data = await response.json();
        this.handleSuccess(data);
      } catch (err) {
        console.error("Rugby registration failed:", err);
        this.emitToast(
          "Registration failed",
          err?.message || "Network error. Check your connection and try again.",
          "error",
        );
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

